import { createElement } from "react";
import { fmtDate, fmtDurationShort, distanceUnit, distanceValue, weightValue } from "../../lib/format";

import { CHART, POWER_DURATION_SERIES, TRAINING_LOAD_SERIES } from "../../lib/designTokens";

export const PROGRESS_CHART_MARGIN = { top: 10, right: 10, left: -10, bottom: 4 };
export const PROGRESS_CHART_TICK = { fontSize: 10, fill: CHART.tick, fontFamily: "JetBrains Mono" };
export const PROGRESS_CHART_GRID_PROPS = { stroke: CHART.grid, strokeDasharray: "3 3", vertical: false };
export const PROGRESS_AXIS_LINE = { stroke: CHART.axis };
export const PROGRESS_Y_AXIS_WIDTH = 48;
export const PROGRESS_HISTORY_Y_AXIS_WIDTH = 40;

export const CHART_MIN_HEIGHT = {
  volume: 288,
  training: 256,
  history: 208,
  power: 288,
};

export function getXAxisInterval(dataLength) {
  if (dataLength <= 5) return 0;
  if (dataLength <= 10) return 1;
  return Math.max(0, Math.floor(dataLength / 6) - 1);
}

export function formatChartDateTick(iso, _index, total) {
  if (!iso) return "";
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    if (total > 10) {
      return date.toLocaleDateString(undefined, { month: "numeric", day: "numeric" });
    }
    if (total > 6) {
      return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    }
    return fmtDate(iso);
  } catch {
    return "";
  }
}

export function buildNumericDomain(rows, keys, { includeZero = true } = {}) {
  const values = [];
  rows.forEach((row) => {
    keys.forEach((key) => {
      const value = row?.[key];
      if (value != null && Number.isFinite(Number(value))) {
        values.push(Number(value));
      }
    });
  });

  if (values.length === 0) {
    return [0, 1];
  }

  let min = Math.min(...values);
  let max = Math.max(...values);

  if (min === max) {
    if (min === 0) return [0, 1];
    const pad = Math.abs(min) * 0.1 || 1;
    return [min - pad, max + pad];
  }

  if (includeZero) {
    min = Math.min(0, min);
  }
  const pad = (max - min) * 0.05 || 1;
  return [min, max + pad];
}

export function createLatestPointDot(totalPoints, color, stroke = CHART.dotStroke) {
  return (props) => {
    const { cx, cy, index } = props;
    if (cx == null || cy == null) return null;
    if (totalPoints === 1 || index === totalPoints - 1) {
      return createElement("circle", {
        cx,
        cy,
        r: 4,
        fill: color,
        stroke,
        strokeWidth: 1.5,
      });
    }
    return null;
  };
}

export function lineSeriesDot(totalPoints, color) {
  if (totalPoints === 1) {
    return { r: 4, fill: color, stroke: CHART.dotStroke, strokeWidth: 1.5 };
  }
  return createLatestPointDot(totalPoints, color);
}

export function formatVolumeMetricValue(metric, value, unitSystem) {
  if (value == null || !Number.isFinite(Number(value))) return null;
  if (metric === "distance_m") {
    return `${Number(value).toFixed(2)} ${distanceUnit(unitSystem)}`;
  }
  if (metric === "moving_s" || metric === "elapsed_s") {
    return fmtDurationShort(value);
  }
  if (metric === "elevation_m") {
    return `${Math.round(value).toLocaleString()} ${unitSystem === "imperial" ? "ft" : "m"}`;
  }
  if (metric === "calories") {
    return `${Math.round(value).toLocaleString()} kcal`;
  }
  if (metric === "activity_count") {
    return `${value} activities`;
  }
  return String(value);
}

export function formatTooltipNumber(value, { decimals = 2, unit = "" } = {}) {
  if (value == null || !Number.isFinite(Number(value))) return null;
  const numeric = Number(value);
  const formatted =
    decimals === 0 || Number.isInteger(numeric)
      ? String(Math.round(numeric))
      : numeric.toFixed(decimals);
  return unit ? `${formatted} ${unit}` : formatted;
}

export function buildVolumeChartData(periods, unitSystem) {
  return periods.map((period) => {
    const distance =
      period.distance_m != null ? distanceValue(period.distance_m, unitSystem) : period.distance_m;
    const elevation =
      period.elevation_m != null
        ? unitSystem === "imperial"
          ? period.elevation_m * 3.28084
          : period.elevation_m
        : period.elevation_m;
    return {
      ...period,
      distance_m: distance,
      elevation_m: elevation,
    };
  });
}

export function buildMetricHistorySeries(metricHistory, unitSystem) {
  return {
    ftp: (metricHistory?.ftp || []).map((point) => ({
      date: point.effectiveDate,
      value: point.value,
    })),
    weight: (metricHistory?.weight || []).map((point) => ({
      date: point.effectiveDate,
      value: weightValue(point.value, unitSystem),
    })),
    wkg: (metricHistory?.wattsPerKg || []).map((point) => ({
      date: point.effectiveDate,
      value: point.wattsPerKg,
    })),
  };
}

export function buildPowerDurationChartRows(history) {
  return history.map((row) => ({
    ...row,
    measuredWatts: row.powerSource === "measured" ? row.bestPowerWatts : null,
    smartTrainerWatts: row.powerSource === "smart_trainer" ? row.bestPowerWatts : null,
    estimatedWatts: row.powerSource === "estimated" ? row.bestPowerWatts : null,
    unknownWatts: row.powerSource === "unknown" ? row.bestPowerWatts : null,
  }));
}

export function powerDurationHasChartValues(rows) {
  return rows.some(
    (row) =>
      row.measuredWatts != null ||
      row.smartTrainerWatts != null ||
      row.estimatedWatts != null ||
      row.unknownWatts != null
  );
}

export { POWER_DURATION_SERIES, TRAINING_LOAD_SERIES } from "../../lib/designTokens";
